import { useLocale, useTranslations } from 'next-intl';
import { Button } from '~/components/Button';
import type { Locale } from '~/i18n/i18n-config';
import { useRouter } from '~/i18n/navigation';
import { api } from '~/libs/api';
import { usePlaygroundActions, usePlaygroundContext, usePlaygroundCurrentFullTrip, usePlaygroundCurrentSkeletonTrip, usePlaygroundCurrentTripIndex, usePlaygroundUIState } from '~/stores/playgroundStore';
import { trackEvent } from '~/utils';
type EditTripButtonProps = {
  variant?: 'small' | 'fullWidth';
  ctaText?: string; // if provided, overrides the default text
  isDisabled?: boolean;
  onBeforeNavigate?: () => 'block' | 'continue';
  onAfterTripBuilt?: (fullTripId: string) => void;
};
export const EditTripButton = ({
  variant = 'fullWidth',
  ctaText = '',
  isDisabled = false,
  onBeforeNavigate,
  onAfterTripBuilt
}: EditTripButtonProps) => {
  const t = useTranslations();
  const locale = useLocale() as Locale;
  const currentTripIndex = usePlaygroundCurrentTripIndex();
  const {
    setShowAnimLoader,
    closeDrawer,
    showDialog,
    setFullTrip
  } = usePlaygroundActions();
  const {
    isOverviewSubmitting,
    isOverviewDisabled
  } = usePlaygroundUIState();
  const fullTrip = usePlaygroundCurrentFullTrip();
  const skeletonTrip = usePlaygroundCurrentSkeletonTrip();
  const playgroundContext = usePlaygroundContext();
  const buildJourneyFromSkeleton = api.playground.buildJourneyFromSkeleton.useMutation();
  const router = useRouter();
  const isSeeTripDisabled = isDisabled || isOverviewSubmitting || isOverviewDisabled || buildJourneyFromSkeleton.isLoading;
  const csUser = typeof window !== 'undefined' ? sessionStorage?.getItem('csUser') ?? '' : '';
  const navigateToTripPage = async () => {
    try {
      if (!fullTrip) {
        setShowAnimLoader(true);
        const newFullTrip = await buildJourneyFromSkeleton.mutateAsync({
          csUser,
          playgroundInputContext: playgroundContext,
          skeletonTripId: skeletonTrip.uniqueId,
          locale
        });
        if (newFullTrip.items.length) {
          setFullTrip(skeletonTrip.uniqueId, newFullTrip);
        } else {
          throw new Error('Invalid trip');
        }
        closeDrawer();

        // Already have valid trip - navigate
        trackEvent(`playground_trip${currentTripIndex + 1}_edit`, {
          event_category: 'Playground',
          event_label: `playground_trip${currentTripIndex + 1}_edit`
        });
        onAfterTripBuilt?.(newFullTrip.uniqueId);
        const url = `/journey/${newFullTrip.uniqueId}?source=playground`;
        router.push(url);
      } else if (fullTrip?.items.length) {
        closeDrawer();
        trackEvent(`playground_trip${currentTripIndex + 1}_edit`, {
          event_category: 'Playground',
          event_label: `playground_trip${currentTripIndex + 1}_edit`
        });
        onAfterTripBuilt?.(fullTrip.uniqueId);
        const url_0 = `/journey/${fullTrip.uniqueId}?source=playground`;
        router.push(url_0);
        setShowAnimLoader(false);
      } else {
        // else it's an invalid trip - no point retriggering full build as it will only error again
        throw new Error('invalid trip - no items');
      }
    } catch (_error) {
      showDialog('couldnt-create-trip');
    } finally {
      setShowAnimLoader(false);
      closeDrawer();
    }
  };
  return <Button size={variant} variant="primary" onPress={() => {
    if (onBeforeNavigate?.() === 'block') return;
    navigateToTripPage();
  }} isDisabled={isSeeTripDisabled}>
      {ctaText || t('playground.emailCapture.cta')}
    </Button>;
};